import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X, Edit, Trash2, Building2, ShoppingCart, FileText, Send, Check, Search, Eye, Share2, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { openAuthenticatedPdf } from "../utils/pdf";
import { canShareFiles, shareFileNative } from "../utils/share";
import { DecimalInput } from "./DecimalInput";
import { RecipientsManagerDialog } from "./SupplierRecipients";
import { DatePickerIT } from "./DatePickerIT";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHARE_SUPPORTED = canShareFiles();

/** Nome file standard: "[Fornitore] - ORD 00027 - 21-07-2026.pdf" */
export const supplierOrderPdfFilename = (o) => {
  const sup = (o.supplier?.business_name || "Fornitore").replace(/[^A-Za-z0-9 ]/g, "").trim().slice(0, 25) || "Fornitore";
  const d = (o.created_at || "").slice(0, 10);
  const dd = d ? `${d.slice(8, 10)}-${d.slice(5, 7)}-${d.slice(0, 4)}` : "";
  const num = o.progressive_number != null ? `ORD ${String(o.progressive_number).padStart(5, "0")}` : (o.order_number || "ORD");
  return `${sup} - ${num}${dd ? ` - ${dd}` : ""}.pdf`;
};

const SUP_STATUS_LABEL = { draft: "Bozza", sent: "Inviato fornitore", received: "Ricevuto", cancelled: "Annullato" };
const SUP_STATUS_COLOR = {
  draft: "bg-yellow-100 text-yellow-700",
  sent: "bg-blue-100 text-blue-700",
  received: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

// ============ SUPPLIERS LIST + FORM ============

export const SuppliersTab = ({ canEdit = true, canDelete = false }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/suppliers`); setSuppliers(r.data || []); }
    catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Eliminare fornitore?")) return;
    try { await axios.delete(`${API}/suppliers/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const filtered = suppliers.filter(s => !search || s.business_name?.toLowerCase().includes(search.toLowerCase()) || s.codice_fornitore?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2"><Building2 className="w-5 h-5 text-[#8A1C2A]" /> Fornitori</h2>
          <p className="text-stone-500 text-sm">{suppliers.length} fornitori attivi</p>
        </div>
        {canEdit && <Button onClick={() => setCreating(true)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="new-supplier-btn"><Plus className="w-4 h-4 mr-1" /> Nuovo fornitore</Button>}
      </div>
      <div className="mb-3 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input placeholder="Cerca fornitore..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white border-stone-300" />
      </div>
      <Card className="bg-white border-stone-200 overflow-hidden">
        {loading ? <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
          : filtered.length === 0 ? <div className="p-8 text-center text-stone-500">Nessun fornitore</div>
            : (
              <>
                <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-stone-50 border-b border-stone-200"><tr>
                    <th className="text-left p-3 text-xs font-semibold">Codice</th>
                    <th className="text-left p-3 text-xs font-semibold">Ragione sociale</th>
                    <th className="text-left p-3 text-xs font-semibold">P.IVA</th>
                    <th className="text-left p-3 text-xs font-semibold">Contatti</th>
                    <th className="text-left p-3 text-xs font-semibold">Pagamento</th>
                    <th className="w-24"></th>
                  </tr></thead>
                  <tbody>{filtered.map(s => (
                    <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`supplier-row-${s.codice_fornitore}`}>
                      <td className="p-3 font-mono text-xs text-[#8A1C2A]">{s.codice_fornitore}</td>
                      <td className="p-3 font-medium">{s.business_name}{s.contact_name && <div className="text-xs text-stone-500">{s.contact_name}</div>}</td>
                      <td className="p-3 font-mono text-xs">{s.vat_number}</td>
                      <td className="p-3 text-xs"><div>{s.email}</div><div className="text-stone-500">{s.phone}</div></td>
                      <td className="p-3 text-xs">{s.payment_terms || "—"}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {canEdit && <Button size="icon" variant="ghost" onClick={() => setEditing(s)}><Edit className="w-4 h-4 text-stone-500" /></Button>}
                        {canDelete && <Button size="icon" variant="ghost" onClick={() => del(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
                </div>
                <div className="md:hidden divide-y divide-stone-100">{filtered.map(s => (
                  <div key={s.id} className="p-3" data-testid={`supplier-card-${s.codice_fornitore}`}>
                    <p className="font-mono text-xs text-[#8A1C2A]">{s.codice_fornitore}</p>
                    <p className="font-medium">{s.business_name}</p>
                    <p className="text-xs text-stone-600">{s.email} · {s.phone}</p>
                    <p className="text-xs text-stone-500">{s.payment_terms || "—"}</p>
                    <div className="flex items-center gap-1 mt-2 -ml-1">
                      {canEdit && <Button size="sm" variant="ghost" onClick={() => setEditing(s)} className="text-xs"><Edit className="w-3 h-3 mr-1" /> Modifica</Button>}
                      {canDelete && <Button size="sm" variant="ghost" onClick={() => del(s.id)} className="text-xs text-red-600"><Trash2 className="w-3 h-3 mr-1" /> Elimina</Button>}
                    </div>
                  </div>
                ))}</div>
              </>
            )}
      </Card>
      {(creating || editing) && <SupplierForm supplier={editing} onClose={() => { setCreating(false); setEditing(null); }} onSaved={() => { setCreating(false); setEditing(null); load(); }} />}
    </div>
  );
};

const SupplierForm = ({ supplier, onClose, onSaved }) => {
  const [form, setForm] = useState(supplier || { business_name: "", contact_name: "", vat_number: "", fiscal_code: "", email: "", phone: "", address: "", city: "", cap: "", province: "", payment_terms: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.business_name || !form.phone) { toast.error("Ragione sociale e telefono obbligatori"); return; }
    setSaving(true);
    try {
      if (supplier?.id) await axios.put(`${API}/suppliers/${supplier.id}`, form);
      else await axios.post(`${API}/suppliers`, form);
      toast.success(supplier?.id ? "Aggiornato" : "Creato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="supplier-form">
        <DialogHeader><DialogTitle>{supplier?.id ? "Modifica fornitore" : "Nuovo fornitore"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Label>Ragione sociale *</Label><Input value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} className="bg-white border-stone-300" data-testid="sup-business" /></div>
          <div><Label>P. IVA</Label><Input value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Codice fiscale</Label><Input value={form.fiscal_code} onChange={e => setForm({ ...form, fiscal_code: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Referente</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Telefono *</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-white border-stone-300" data-testid="sup-phone" /></div>
          <div><Label>Indirizzo</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Città</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>CAP</Label><Input value={form.cap} onChange={e => setForm({ ...form, cap: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Provincia</Label><Input value={form.province} onChange={e => setForm({ ...form, province: e.target.value })} className="bg-white border-stone-300" /></div>
          <div className="md:col-span-2"><Label>Condizioni di pagamento</Label><Input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} className="bg-white border-stone-300" placeholder="Es. 30gg FM" /></div>
          <div className="md:col-span-2"><Label>Note</Label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 text-sm bg-white border border-stone-300 rounded-md" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button><Button onClick={submit} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="sup-save-btn">{saving ? "..." : "Salva"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ SUPPLIER ORDERS ============

export const SupplierOrdersTab = ({ isAdmin = false }) => {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filter, setFilter] = useState(isAdmin ? "draft" : "all");
  const [creating, setCreating] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/supplier-orders${filter !== "all" ? `?status=${filter}` : ""}`);
      setOrders(r.data || []);
      const [s, p, c, pr, lo] = await Promise.all([
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/company-profiles`),
        axios.get(`${API}/delivery-locations`),
      ]);
      setSuppliers(s.data || []);
      setProducts(p.data || []);
      setCategories(c.data || []);
      setProfiles(pr.data || []);
      setLocations(lo.data || []);
    } catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/supplier-orders/${id}/status`, { status: newStatus });
      toast.success("Stato aggiornato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const preview = (id) => openAuthenticatedPdf(`/supplier-orders/${id}/pdf`);
  const downloadPdf = (order) => {
    openAuthenticatedPdf(`/supplier-orders/${order.id}/pdf`, { download: true, filename: supplierOrderPdfFilename(order) });
  };
  const shareNative = async (order) => {
    try {
      const r = await axios.get(`${API}/supplier-orders/${order.id}/pdf`, { responseType: "blob" });
      const filename = supplierOrderPdfFilename(order);
      await shareFileNative({ blob: r.data, filename, title: filename });
    } catch { toast.error("Errore condivisione PDF"); }
  };
  const shareSupplierWhatsapp = (order) => {
    const phone = (order.supplier?.phone || "").replace(/[^0-9+]/g, "");
    const text = encodeURIComponent(`Buongiorno ${order.supplier?.business_name || ""},\nin allegato l'ordine ${order.order_number}.\nGrazie!`);
    window.open(phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-[#8A1C2A]" /> Ordini Fornitori</h2>
          <p className="text-stone-500 text-sm">{orders.length} ordini</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-white border-stone-300" data-testid="sup-order-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="draft">Bozza</SelectItem>
              <SelectItem value="sent">Inviato</SelectItem>
              <SelectItem value="received">Ricevuto</SelectItem>
              <SelectItem value="cancelled">Annullato</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setRecipientsOpen(true)} className="border-[#C5A059] text-[#8A1C2A]" data-testid="manage-recipients-btn">
            <MapPin className="w-4 h-4 mr-1" /> Destinatari &amp; Consegne
          </Button>
          <Button onClick={() => setCreating(true)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="new-sup-order-btn"><Plus className="w-4 h-4 mr-1" /> Nuovo ordine</Button>
        </div>
      </div>
      {loading ? <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
        : orders.length === 0 ? <Card className="bg-white border-stone-200 p-8 text-center text-stone-500">Nessun ordine</Card>
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {orders.map(o => (
                <Card key={o.id} className="bg-white border-stone-200 p-3" data-testid={`sup-order-${o.order_number}`}>
                  <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
                    <p className="font-mono font-semibold text-[#8A1C2A] text-sm">{o.order_number}</p>
                    <Badge className={SUP_STATUS_COLOR[o.status]}>{SUP_STATUS_LABEL[o.status]}</Badge>
                  </div>
                  <p className="font-medium truncate">{o.supplier?.business_name}</p>
                  <p className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString('it-IT')} · {o.items?.length || 0} righe</p>
                  {o.deliver_to?.label && <p className="text-xs text-stone-600">📍 {o.deliver_to.label}</p>}
                  <p className="text-xl font-bold text-[#8A1C2A] mt-1">€ {(o.total_amount || 0).toFixed(2)}</p>
                  {o.notes && <p className="text-xs italic text-stone-500 mt-1">📝 {o.notes}</p>}
                  <div className="flex flex-wrap items-center gap-1 mt-2 -ml-1">
                    <Button size="sm" variant="ghost" onClick={() => preview(o.id)} className="text-xs"><Eye className="w-3 h-3 mr-1" /> PDF</Button>
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(o)} className="text-xs"><FileText className="w-3 h-3 mr-1" /> Scarica</Button>
                    {(o.status === "draft" || (isAdmin && o.status === "sent")) && <Button size="sm" variant="ghost" onClick={() => setEditingOrder(o)} className="text-xs text-stone-700" data-testid={`edit-supplier-order-${o.order_number}`}><Edit className="w-3 h-3 mr-1" /> Modifica</Button>}
                    {SHARE_SUPPORTED && <Button size="sm" variant="ghost" onClick={() => shareNative(o)} className="text-xs text-emerald-700" data-testid={`share-supplier-${o.order_number}`}><Share2 className="w-3 h-3 mr-1" /> Condividi</Button>}
                    {isAdmin && o.status === "draft" && <Button size="sm" onClick={() => updateStatus(o.id, "sent")} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`send-supplier-${o.order_number}`}><Send className="w-3 h-3 mr-1" /> Inviato</Button>}
                    {isAdmin && o.status === "sent" && <Button size="sm" variant="ghost" onClick={() => shareSupplierWhatsapp(o)} className="text-xs text-green-700">📱 WA</Button>}
                    {(isAdmin || o.status === "draft") && o.status !== "received" && o.status !== "cancelled" && <Button size="sm" variant="ghost" onClick={() => updateStatus(o.id, "received")} className="text-xs text-emerald-700"><Check className="w-3 h-3 mr-1" /> Ricevuto</Button>}
                  </div>
                </Card>
              ))}
            </div>
          )}
      {(creating || editingOrder) && <SupplierOrderForm order={editingOrder} suppliers={suppliers} products={products} categories={categories} profiles={profiles} locations={locations} onManageRecipients={() => setRecipientsOpen(true)} onClose={() => { setCreating(false); setEditingOrder(null); }} onSaved={() => { setCreating(false); setEditingOrder(null); load(); }} />}
      <RecipientsManagerDialog open={recipientsOpen} onClose={() => setRecipientsOpen(false)} onChanged={load} />
    </div>
  );
};

const SupplierOrderForm = ({ order = null, suppliers, products, categories = [], profiles = [], locations = [], onManageRecipients, onClose, onSaved }) => {
  const [supplierId, setSupplierId] = useState(order?.supplier_id || "");
  const [items, setItems] = useState(order?.items ? order.items.map(it => ({ ...it })) : []);
  const [notes, setNotes] = useState(order?.notes || "");
  const [expectedArrival, setExpectedArrival] = useState(order?.expected_arrival_date || "");
  const [search, setSearch] = useState("");
  const [companyProfileId, setCompanyProfileId] = useState(order ? (order.bill_to?.id || "") : (profiles[0]?.id || ""));
  const [deliveryLocationId, setDeliveryLocationId] = useState(order ? (order.deliver_to?.id || "") : (locations[0]?.id || ""));
  const [saving, setSaving] = useState(false);

  const catMap = React.useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.name])), [categories]);

  const q = search.toLowerCase();
  const filteredProducts = products.filter(p => !q
    || p.code.toLowerCase().includes(q)
    || p.description.toLowerCase().includes(q)
    || (catMap[p.category_id] || "").toLowerCase().includes(q)
  ).slice(0, 40);

  const addFromCatalog = (p) => {
    setItems(prev => [...prev, { product_id: p.id, code: p.code, description: p.description, quantity: 1, unit: p.unit || "Kg", unit_price: p.acq_price || 0, line_total: p.acq_price || 0, note: "" }]);
  };
  const addFree = () => setItems(prev => [...prev, { product_id: null, code: "LIBERO", description: "", quantity: 1, unit: "Kg", unit_price: 0, line_total: 0, note: "" }]);
  const updateItem = (idx, field, value) => {
    setItems(prev => {
      const arr = [...prev];
      arr[idx] = { ...arr[idx], [field]: value };
      arr[idx].line_total = +((arr[idx].quantity || 0) * (arr[idx].unit_price || 0)).toFixed(2);
      return arr;
    });
  };
  const removeItem = idx => setItems(items.filter((_, i) => i !== idx));

  const total = items.reduce((s, it) => s + (it.line_total || 0), 0);

  const submit = async () => {
    if (!supplierId) { toast.error("Seleziona fornitore"); return; }
    if (items.length === 0 || items.some(it => !it.description || it.quantity <= 0)) { toast.error("Aggiungi almeno una riga valida"); return; }
    setSaving(true);
    try {
      const payload = {
        supplier_id: supplierId, items, notes,
        expected_arrival_date: expectedArrival || null,
        company_profile_id: companyProfileId || null,
        delivery_location_id: deliveryLocationId || null,
      };
      if (order?.id) {
        await axios.put(`${API}/supplier-orders/${order.id}`, payload);
        toast.success(`Ordine ${order.order_number} aggiornato`);
      } else {
        await axios.post(`${API}/supplier-orders`, payload);
        toast.success("Ordine fornitore creato (bozza in attesa di admin)");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-white max-h-[95vh] overflow-y-auto" data-testid="sup-order-form">
        <DialogHeader><DialogTitle>{order ? `Modifica ordine ${order.order_number}` : "Nuovo ordine fornitore"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Fornitore *</Label>
            <Select value={supplierId || "__none__"} onValueChange={v => setSupplierId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="bg-white border-stone-300" data-testid="sup-order-select-supplier"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Seleziona —</SelectItem>
                {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.codice_fornitore} · {s.business_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Arrivo previsto</Label><DatePickerIT value={expectedArrival} onChange={setExpectedArrival} data-testid="supplier-order-arrival" /></div>
        </div>
        {/* Destinatario + destinazione di consegna (compaiono nel PDF) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-stone-50 border border-stone-200 rounded-lg p-3">
          <div>
            <Label className="text-xs">🏢 Ragione sociale destinataria</Label>
            <Select value={companyProfileId || "__none__"} onValueChange={v => setCompanyProfileId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="bg-white border-stone-300 h-9" data-testid="sup-order-profile-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Nessuna —</SelectItem>
                {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.business_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">📍 Consegna presso</Label>
            <Select value={deliveryLocationId || "__none__"} onValueChange={v => setDeliveryLocationId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="bg-white border-stone-300 h-9" data-testid="sup-order-location-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Nessuna —</SelectItem>
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.label}{l.receiving_hours ? ` · 🕒 ${l.receiving_hours}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="sm:col-span-2 text-[11px] text-stone-500 -mt-1">
            Indirizzo, contatti e orari di ricevimento compariranno nel PDF dell&apos;ordine.{" "}
            <button type="button" onClick={onManageRecipients} className="underline text-[#8A1C2A]" data-testid="open-recipients-from-form">Gestisci destinatari e sedi</button>
          </p>
        </div>
        <div className="border-t border-stone-200 pt-3">
          <Label>Cerca articolo dal catalogo (tutti i prodotti, anche non a listino)</Label>
          <div className="flex gap-2 mt-1 flex-wrap">
            <Input placeholder="Codice, descrizione o categoria..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1 min-w-[200px] bg-white border-stone-300" data-testid="sup-product-search" />
            <Button onClick={addFree} variant="outline" className="border-[#C5A059] text-[#8A1C2A]" data-testid="sup-add-free"><Plus className="w-4 h-4 mr-1" /> Articolo libero</Button>
          </div>
          {search && (
            <div className="max-h-40 overflow-y-auto border border-stone-200 rounded mt-2">
              {filteredProducts.map(p => (
                <button key={p.id} onClick={() => addFromCatalog(p)} className="w-full text-left p-2 hover:bg-stone-50 border-b border-stone-100 flex items-center justify-between gap-2 text-sm" data-testid={`sup-add-catalog-${p.code}`}>
                  <span className="min-w-0">
                    <span className="font-mono text-xs text-[#8A1C2A] mr-2">{p.code}</span>{p.description}
                    {catMap[p.category_id] && <Badge className="ml-2 bg-stone-100 text-stone-600 border border-stone-200 text-[10px] align-middle">{catMap[p.category_id]}</Badge>}
                  </span>
                  <Plus className="w-4 h-4 text-stone-400 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-stone-200 pt-3">
          <Label>Righe ordine</Label>
          {items.length === 0 ? <p className="text-sm text-stone-500 italic mt-2">Nessuna riga. Aggiungi articoli dal catalogo o crea un articolo libero.</p>
            : (
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-stone-50"><tr><th className="text-left p-2 text-xs">Codice</th><th className="text-left p-2 text-xs">Descrizione</th><th className="text-right p-2 text-xs w-20">Q.tà</th><th className="text-left p-2 text-xs w-16">UM</th><th className="text-right p-2 text-xs w-24">€/UM</th><th className="text-right p-2 text-xs w-24">Tot.</th><th className="text-left p-2 text-xs">Note</th><th className="w-8"></th></tr></thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={idx} className="border-t border-stone-100">
                        <td className="p-1 font-mono text-xs">{it.code === "LIBERO" ? <Input value={it.code} onChange={e => updateItem(idx, "code", e.target.value)} className="h-8 text-xs bg-white border-stone-300" /> : it.code}</td>
                        <td className="p-1"><Input value={it.description} onChange={e => updateItem(idx, "description", e.target.value)} className="h-8 text-xs bg-white border-stone-300" /></td>
                        <td className="p-1"><DecimalInput value={it.quantity} onValue={n => updateItem(idx, "quantity", n)} className="h-8 text-xs text-right bg-white border-stone-300" /></td>
                        <td className="p-1"><Input value={it.unit} onChange={e => updateItem(idx, "unit", e.target.value)} className="h-8 text-xs bg-white border-stone-300" /></td>
                        <td className="p-1"><DecimalInput value={it.unit_price} onValue={n => updateItem(idx, "unit_price", n)} className="h-8 text-xs text-right bg-white border-stone-300" /></td>
                        <td className="p-1 text-right text-xs font-semibold text-[#8A1C2A]">{(it.line_total || 0).toFixed(2)}</td>
                        <td className="p-1"><Input value={it.note} onChange={e => updateItem(idx, "note", e.target.value)} className="h-8 text-xs bg-white border-stone-300" /></td>
                        <td className="p-1"><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeItem(idx)}><X className="w-3 h-3 text-red-500" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="bg-stone-50 border-t-2 border-stone-200"><td colSpan="5" className="p-2 text-right font-semibold">Totale</td><td className="p-2 text-right font-bold text-[#8A1C2A] text-lg">€ {total.toFixed(2)}</td><td colSpan="2"></td></tr></tfoot>
                </table>
              </div>
            )}
        </div>
        <div><Label>Note ordine</Label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 text-sm bg-white border border-stone-300 rounded-md" /></div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button><Button onClick={submit} disabled={saving || items.length === 0} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="sup-order-submit">{saving ? "..." : order ? "Salva modifiche" : "Crea ordine (bozza)"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
